import useConfirmOnlineOrder from '@client/page/management/online-order/detail/logic/use-confirm-online-order.ts';
import { useCallback } from 'react';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';
import { useTranslate } from '@tolgee/react';
import { OnlineOrderItemFragment } from '@client/graphql/types/graphql.ts';
import { useModalManagerContext } from '@packages/ui/modal/modal-manager-context.ts';
import OnlineOrderConfirmProofDialog from '@client/page/management/online-order/detail/component/online-order-confirm-proof-dialog.tsx';

const OnlineOrderActionConfirm = (props: {
  orderID: string;
  preparingTime?: number | null;
  onClose?: () => void;
  proof?: Pick<
    OnlineOrderItemFragment,
    'manualTransferProofImageOrigin' | 'manualTransferProofImageThumbnail' | 'manualTransferSenderName'
  > | null;
}) => {
  const { orderID, preparingTime, onClose, proof } = props;

  const { showAlert } = useNotificationCenter();
  const dialogManager = useModalManagerContext();

  const [confirm, { loading }] = useConfirmOnlineOrder();
  const { t } = useTranslate();

  const doConfirm = useCallback(async () => {
    if (preparingTime == null) {
      showAlert({
        status: 'error',
        title: t('alert.title.error', 'Error'),
        message: t(
          'online-order.confirm.alert.preparing-time-null.message',
          'You have not set preparing time, please select again.',
        ),
      });
      return;
    }
    await confirm({
      variables: {
        id: orderID,
        input: {
          preparingTime: preparingTime,
        },
      },
    })
      .then(() => {
        showAlert({
          status: 'success',
          title: t('alert.title.success'),
          message: t('online-order.detail.action.confirm.alert.message'),
        });
        onClose?.();
      })
      .catch((e: Error) => {
        showAlert({
          status: 'error',
          title: t('alert.title.error'),
          message: e.message,
        });
        throw e;
      });
  }, [confirm, orderID, preparingTime, showAlert, onClose, t]);

  const confirmHandler = useCallback(() => {
    if (proof) {
      dialogManager.showDialog((close) => (
        <OnlineOrderConfirmProofDialog
          order={proof}
          onConfirm={async () => {
            await doConfirm();
            close();
          }}
          onCancel={close}
        />
      ));
      return;
    }
    void doConfirm();
  }, [proof, dialogManager, doConfirm]);

  return (
    <PrimaryButton onClick={confirmHandler} disabled={loading}>
      {t('online-order.detail.action.confirm.button')}
    </PrimaryButton>
  );
};

export default OnlineOrderActionConfirm;
